import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'gatsby';

import {
    OPEN_CONSENT_EVENT,
    getConsent,
    isEeaVisitor,
    setConsent,
} from '../../utils/consent';
import {
    loadTrackers,
    suppressTrackers,
    unsuppressTrackers,
} from '../../utils/trackers';

import {
    AcceptButton,
    ConsentActions,
    ConsentCard,
    ConsentText,
    ConsentTitle,
    DeclineButton,
} from './CookieConsent.styles';

/**
 * Analytics consent gate.
 *
 * Visitors outside the EEA are tracked on load and never see this card.
 * Visitors inside it get no trackers at all until they choose, and can reopen
 * the card at any time from the footer link.
 */
const CookieConsent: React.FC = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const consent = getConsent();

        if (consent === 'granted') {
            unsuppressTrackers();
            loadTrackers();
            return;
        }

        if (consent === 'denied') {
            suppressTrackers();
            return;
        }

        if (!isEeaVisitor()) {
            setConsent('granted');
            loadTrackers();
            return;
        }

        setVisible(true);
    }, []);

    useEffect(() => {
        const open = () => setVisible(true);

        window.addEventListener(OPEN_CONSENT_EVENT, open);

        return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
    }, []);

    const handleAccept = useCallback(() => {
        setConsent('granted');
        unsuppressTrackers();
        loadTrackers();
        setVisible(false);
    }, []);

    const handleDecline = useCallback(() => {
        setConsent('denied');
        suppressTrackers();
        setVisible(false);
    }, []);

    if (!visible) {
        return null;
    }

    return (
        <ConsentCard
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-consent-title"
        >
            <ConsentTitle id="cookie-consent-title">
                Analytics cookies?
            </ConsentTitle>

            <ConsentText>
                We use analytics cookies to understand how the site is used.
                They are only set if you accept. See our{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>.
            </ConsentText>

            <ConsentActions>
                <DeclineButton type="button" onClick={handleDecline}>
                    Decline
                </DeclineButton>
                <AcceptButton type="button" onClick={handleAccept}>
                    Accept
                </AcceptButton>
            </ConsentActions>
        </ConsentCard>
    );
};

export default CookieConsent;
